import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { del } from "common/helpers/HTTP";

import type { ListLeadsTotals } from "../types";

async function removeExclusionRequest(
  workspaceId: string,
  listId: string,
  urn: string,
  abortSignal?: AbortSignal,
) {
  //dropping the exclusion lets the list's sources target the lead again
  await del(
    `workspaces/${workspaceId}/lists/${listId}/exclusions/${urn}`,
    abortSignal,
  );
}

export default function useExcludedListLeadsMutations(listId: string) {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { listsKeys } = getQueryKeys(workspaceId);

  const {
    mutateAsync: removeListExclusion,
    isPending: isRemovingListExclusion,
  } = useMutation({
    mutationFn: ({
      urn,
      abortSignal,
    }: {
      urn: string;
      abortSignal?: AbortSignal;
    }) => removeExclusionRequest(workspaceId, listId, urn, abortSignal),
    onSuccess: () => {
      queryClient.setQueryData<ListLeadsTotals>(
        listsKeys.leadsTotals(listId),
        (prevTotals) =>
          prevTotals && {
            ...prevTotals,
            excluded: prevTotals.excluded - 1,
          },
      );
      queryClient.invalidateQueries({
        queryKey: listsKeys.leads(listId, true),
      });
    },
  });

  return { removeListExclusion, isRemovingListExclusion };
}
